import { useMemo, useState } from "react";
import { classNames } from "../../UI/Utils/classNames.ts";
import { WeekdayHourAvailability } from "./availabilityStats.ts";
import { shareOf } from "./shareOf.ts";
import { formatHours, formatShare } from "./activityFormat.ts";
import { HEATMAP_RAMP } from "./teacherActivityColors.ts";
import { SegmentedControl } from "../../UI/SegmentedControl.tsx";

type HeatmapMode = "offered" | "booked";
type WeekdayHour = { weekdayFromSunday: number; hour: number };

const HEATMAP_MODES: { value: HeatmapMode; label: string }[] = [
  { value: "offered", label: "Availability" },
  { value: "booked", label: "Booked share" },
];

const LEGEND_BY_MODE: Record<
  HeatmapMode,
  { title: string; low: string; high: string }
> = {
  offered: {
    title: "Weeks with availability at that hour",
    low: "Rarely",
    high: "Every week",
  },
  booked: {
    title: "Share of the offered time that got booked",
    low: "0%",
    high: "100%",
  },
};

const WEEKDAY_SHORT_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const HOURS_SHOWN_WITHOUT_AVAILABILITY = { first: 8, last: 20 };

function cellKey({ weekdayFromSunday, hour }: WeekdayHour): string {
  return `${weekdayFromSunday}-${hour}`;
}

function hoursToShow(cells: WeekdayHourAvailability[]): number[] {
  const hoursWithAvailability = cells
    .filter((cell) => cell.offeredHours > 0)
    .map((cell) => cell.hour);
  const first = hoursWithAvailability.length
    ? Math.min(...hoursWithAvailability)
    : HOURS_SHOWN_WITHOUT_AVAILABILITY.first;
  const last = hoursWithAvailability.length
    ? Math.max(...hoursWithAvailability)
    : HOURS_SHOWN_WITHOUT_AVAILABILITY.last;
  return Array.from({ length: last - first + 1 }, (_, index) => first + index);
}

function intensityOf(
  cell: WeekdayHourAvailability | undefined,
  mode: HeatmapMode,
  weeksInPeriod: number,
): number | null {
  if (!cell || cell.offeredHours <= 0) return null;
  return mode === "offered"
    ? Math.min(1, cell.offeredHours / weeksInPeriod)
    : (shareOf(cell.bookedHours, cell.offeredHours) ?? 0);
}

function rampColorOf(intensity: number): string {
  const step = Math.min(
    HEATMAP_RAMP.length - 1,
    Math.floor(intensity * HEATMAP_RAMP.length),
  );
  return HEATMAP_RAMP[step];
}

function formatHourRange(hour: number): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(hour)}:00–${pad((hour + 1) % 24)}:00`;
}

function describeCell(
  { weekdayFromSunday, hour }: WeekdayHour,
  cell: WeekdayHourAvailability | undefined,
  weeksInPeriod: number,
): string {
  const when = `${WEEKDAY_NAMES[weekdayFromSunday]} ${formatHourRange(hour)}`;
  if (!cell || cell.offeredHours <= 0) return `${when} · no availability`;
  return `${when} · ${formatHours(cell.offeredHours)} offered over ${weeksInPeriod} weeks · ${formatHours(cell.bookedHours)} booked (${formatShare(shareOf(cell.bookedHours, cell.offeredHours))})`;
}

function HeatmapLegend({ mode }: { mode: HeatmapMode }) {
  const legend = LEGEND_BY_MODE[mode];
  return (
    <div
      className={
        "flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500"
      }
    >
      <span className={"flex items-center gap-1.5"}>
        <span
          className={"w-3 h-3 rounded-sm bg-white border border-slate-200"}
        />
        No availability
      </span>
      <span className={"flex flex-wrap items-center gap-x-1.5 gap-y-1"}>
        <span>{legend.title}:</span>
        <span className={"flex items-center gap-1.5 whitespace-nowrap"}>
          {legend.low}
          <span className={"flex gap-0.5"}>
            {HEATMAP_RAMP.map((color) => (
              <span
                key={color}
                className={"w-3 h-3 rounded-sm"}
                style={{ backgroundColor: color }}
              />
            ))}
          </span>
          {legend.high}
        </span>
      </span>
    </div>
  );
}

export function AvailabilityHeatmap({
  cells,
  weeksInPeriod,
}: {
  cells: WeekdayHourAvailability[];
  weeksInPeriod: number;
}) {
  const [mode, setMode] = useState<HeatmapMode>("offered");
  const [selectedWeekdayHour, setSelectedWeekdayHour] =
    useState<WeekdayHour | null>(null);

  const cellsByKey = useMemo(
    () => new Map(cells.map((cell) => [cellKey(cell), cell])),
    [cells],
  );
  const hours = useMemo(() => hoursToShow(cells), [cells]);

  const selectedCellKey = selectedWeekdayHour
    ? cellKey(selectedWeekdayHour)
    : null;
  const selectedCellDescription = selectedWeekdayHour
    ? describeCell(
        selectedWeekdayHour,
        cellsByKey.get(cellKey(selectedWeekdayHour)),
        weeksInPeriod,
      )
    : null;

  return (
    <div className={"flex flex-col gap-3"}>
      <div className={"flex flex-wrap items-center justify-between gap-2"}>
        <h3 className={"text-sm font-semibold text-slate-700"}>
          When the teacher is available
        </h3>
        <SegmentedControl
          options={HEATMAP_MODES}
          value={mode}
          onChange={setMode}
        />
      </div>

      <div className={"overflow-x-auto"}>
        <div
          className={"grid gap-0.5"}
          style={{
            gridTemplateColumns: `2.5rem repeat(${hours.length}, minmax(0.875rem, 1fr))`,
          }}
          onMouseLeave={() => setSelectedWeekdayHour(null)}
        >
          <span />
          {hours.map((hour) => (
            <span
              key={hour}
              className={"text-[10px] text-slate-400 text-center leading-4"}
            >
              {hour}
            </span>
          ))}
          {WEEKDAY_SHORT_NAMES.map((weekdayName, weekdayFromSunday) => (
            <div key={weekdayName} className={"contents"}>
              <span className={"text-xs text-slate-500 flex items-center pr-1"}>
                {weekdayName}
              </span>
              {hours.map((hour) => {
                const weekdayHour = { weekdayFromSunday, hour };
                const key = cellKey(weekdayHour);
                const cell = cellsByKey.get(key);
                const intensity = intensityOf(cell, mode, weeksInPeriod);
                const selectCell = () => setSelectedWeekdayHour(weekdayHour);
                return (
                  <button
                    key={key}
                    type={"button"}
                    aria-label={describeCell(weekdayHour, cell, weeksInPeriod)}
                    onMouseEnter={selectCell}
                    onFocus={selectCell}
                    onClick={selectCell}
                    className={classNames(
                      "h-6 rounded-sm outline-none",
                      intensity === null
                        ? "bg-white border border-slate-200"
                        : undefined,
                      selectedCellKey === key
                        ? "ring-2 ring-slate-700 ring-offset-1"
                        : undefined,
                    )}
                    style={
                      intensity === null
                        ? undefined
                        : { backgroundColor: rampColorOf(intensity) }
                    }
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <p className={"text-xs text-slate-600 min-h-4"}>
        {selectedCellDescription ?? (
          <span className={"text-slate-400"}>
            Hover or tap a cell to see the hours offered and booked.
          </span>
        )}
      </p>
      <HeatmapLegend mode={mode} />
    </div>
  );
}
